<div class="aca-props">
  <!-- ── one card per section, each with its own tile and summary line ────── -->
  <ng-container
    *ngTemplateOutlet="
      card;
      context: {
        id: 'general',
        icon: 'description',
        tone: 'teal',
        title: 'DMS.PREVIEW.GENERAL' | translate,
        summary: 'DMS.PREVIEW.GENERAL_SUB' | translate,
        rows: generalRows()
      }
    "
  />

  <ng-container
    *ngTemplateOutlet="card; context: { id: 'tags', icon: 'sell', tone: 'blue', title: 'DMS.UI.792FF6' | translate, summary: tagSummary, rows: [] }"
  />

  <ng-container
    *ngTemplateOutlet="
      card;
      context: {
        id: 'classification',
        icon: 'folder',
        tone: 'purple',
        title: 'DMS.PREVIEW.CLASSIFICATION' | translate,
        summary: classificationSummary,
        rows: classificationRows()
      }
    "
  />

  <ng-container
    *ngIf="exifRows().length"
    [ngTemplateOutlet]="card"
    [ngTemplateOutletContext]="{
      id: 'exif',
      icon: 'photo_camera',
      tone: 'amber',
      title: 'DMS.PREVIEW.EXIF' | translate,
      summary: exifSummary,
      rows: exifRows(),
      readonly: true
    }"
  />
</div>

<ng-template #card let-id="id" let-icon="icon" let-tone="tone" let-title="title" let-summary="summary" let-rows="rows" let-readonly="readonly">
  <section class="aca-props__card" [class.aca-props__card--open]="open()[id]">
    <button type="button" class="aca-props__head" (click)="toggle(id)" [attr.aria-expanded]="open()[id]">
      <span class="aca-props__tile" [ngClass]="'aca-props__tile--' + tone"
        ><mat-icon>{{ icon }}</mat-icon></span
      >

      <span class="aca-props__headtext">
        <span class="aca-props__title">{{ title }}</span>
        <span class="aca-props__summary">
          {{ summary }}
          <ng-container *ngIf="id === 'tags' && tags().length">
            <span class="aca-props__dot">•</span>
            <span class="aca-props__hashtag" *ngFor="let tag of tags().slice(0, 3)">#{{ tag.tag }}</span>
          </ng-container>
        </span>
      </span>

      <span
        class="aca-props__edit"
        *ngIf="!readonly"
        role="button"
        tabindex="0"
        (click)="startEdit(id, rows, $event)"
        (keydown.enter)="startEdit(id, rows, $event)"
      >
        <mat-icon>edit</mat-icon>{{ 'DMS.PREVIEW.EDIT' | translate }}
      </span>

      <mat-icon class="aca-props__chevron">{{ open()[id] ? 'expand_less' : 'expand_more' }}</mat-icon>
    </button>

    <div class="aca-props__body" *ngIf="open()[id]">
      <!-- tags are chips rather than rows -->
      <ng-container *ngIf="id === 'tags'">
        <div class="aca-props__tags" *ngIf="tags().length">
          <span class="aca-props__tag" *ngFor="let tag of tags()">
            {{ tag.tag }}
            <button type="button" (click)="removeTag(tag)" [attr.aria-label]="'DMS.UI.BDD0C7' | translate">
              <mat-icon>close</mat-icon>
            </button>
          </span>
        </div>
        <div class="aca-props__tagadd">
          <input
            [(ngModel)]="newTag"
            (keyup.enter)="addTag()"
            [placeholder]="'DMS.UI.631400' | translate"
            [attr.aria-label]="'DMS.UI.631400' | translate"
          />
          <button type="button" class="aca-props__outline" (click)="addTag()" [disabled]="!newTag.trim()">
            <mat-icon>add</mat-icon>{{ 'DMS.HISTORY.ADD_TAG' | translate }}
          </button>
        </div>
      </ng-container>

      <ng-container *ngIf="id !== 'tags'">
        <dl class="aca-props__grid">
          <div class="aca-props__cell" *ngFor="let row of rows; trackBy: trackByKey" [class.aca-props__cell--wide]="row.wide">
            <dt>
              {{ row.label | translate }}
              <span
                class="aca-props__ai"
                *ngIf="row.key && suggested().has(row.key)"
                [matTooltip]="'DMS.ASSISTANT.CLASSIFY_BADGE_HINT' | translate"
                >{{ 'DMS.ASSISTANT.CLASSIFY_BADGE' | translate }}</span
              >
            </dt>
            <dd>
              <!-- reading -->
              <ng-container *ngIf="editing() !== id || !row.key">
                <span *ngIf="!isDate(row)">{{ display(row) }}</span>
                <span *ngIf="isDate(row)">{{ (row.value | acaDate: 'd MMMM y HH:mm') || '—' }}</span>
                <span class="aca-props__kind" *ngIf="row.key === 'cm:name' && row.value">{{ kindLabel }}</span>
              </ng-container>

              <!-- writing -->
              <ng-container *ngIf="editing() === id && row.key">
                <select class="aca-props__input" *ngIf="row.field?.options?.length" [(ngModel)]="draft[row.key]">
                  <option value="">—</option>
                  <option *ngFor="let option of row.field.options" [value]="option.value">{{ option.label | translate }}</option>
                </select>
                <textarea class="aca-props__input" *ngIf="!row.field?.options?.length && row.wide" rows="3" [(ngModel)]="draft[row.key]"></textarea>
                <input class="aca-props__input" *ngIf="!row.field?.options?.length && !row.wide" [(ngModel)]="draft[row.key]" />
              </ng-container>
            </dd>
          </div>
        </dl>

        <div class="aca-props__actions" *ngIf="editing() === id">
          <button
            type="button"
            class="aca-props__outline aca-props__suggest"
            *ngIf="id === 'classification' && canSuggest()"
            [disabled]="suggesting() || saving()"
            [matTooltip]="'DMS.ASSISTANT.CLASSIFY_HINT' | translate"
            (click)="suggest()"
          >
            <mat-icon>auto_awesome</mat-icon>{{ (suggesting() ? 'DMS.ASSISTANT.CLASSIFY_BUSY' : 'DMS.ASSISTANT.CLASSIFY_BUTTON') | translate }}
          </button>
          <button type="button" class="aca-props__outline" (click)="cancelEdit()">{{ 'DMS.UI.7D3094' | translate }}</button>
          <button type="button" class="aca-props__primary" [disabled]="saving()" (click)="save()">
            <mat-icon>save</mat-icon>{{ 'DMS.UI.6EA45A' | translate }}
          </button>
        </div>
      </ng-container>
    </div>
  </section>
</ng-template>
